// Formatting helpers. All output follows the active UI language.
import { intlLocale } from '../i18n';

// Chrome (and other browsers) ship without Albanian date/number data and silently fall back to
// English, so Albanian gets a small built-in formatter whenever the browser cannot do it itself.
const SQ = {
    monthsLong: ['janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'],
    monthsShort: ['jan', 'shk', 'mar', 'pri', 'maj', 'qer', 'kor', 'gush', 'sht', 'tet', 'nën', 'dhj'],
    daysLong: ['e diel', 'e hënë', 'e martë', 'e mërkurë', 'e enjte', 'e premte', 'e shtunë'],
    daysShort: ['Die', 'Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht'],
};
const nativeSupport = {};
const needsFallback = () => {
    const loc = intlLocale();
    if (!loc.startsWith('sq')) return false;
    if (nativeSupport[loc] === undefined) {
        try { nativeSupport[loc] = Intl.DateTimeFormat.supportedLocalesOf([loc]).length > 0; }
        catch { nativeSupport[loc] = false; }
    }
    return !nativeSupport[loc];
};

/** Albanian date in the shape the Intl options ask for, e.g. "e premte, 16 tetor 2026". */
const sqDate = (date, o = {}) => {
    const parts = [];
    const day = o.day === '2-digit' ? String(date.getDate()).padStart(2, '0') : String(date.getDate());
    const month = o.month === 'long' ? SQ.monthsLong[date.getMonth()] : SQ.monthsShort[date.getMonth()];
    if (o.day || o.month) parts.push([o.day ? day : null, o.month ? month : null].filter(Boolean).join(' '));
    if (o.year) parts.push(String(date.getFullYear()));
    const main = parts.join(' ');
    if (!o.weekday) return main;
    const wd = o.weekday === 'short' ? SQ.daysShort[date.getDay()] : SQ.daysLong[date.getDay()];
    return main ? `${wd}, ${main}` : wd;
};

const formatDate = (date, options) =>
    needsFallback() ? sqDate(date, options) : date.toLocaleDateString(intlLocale(), options);

export const fmtDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' && d.length === 10 ? parseLocalDate(d) : new Date(d);
    return formatDate(date, { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Date and time, e.g. "17 Oct 2026, 14:05" (24-hour clock in every language). */
export const fmtDateTime = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return `${formatDate(date, { day: '2-digit', month: 'short', year: 'numeric' })}, ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const fmtDayLong = (date) =>
    formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' });

export const fmtMoney = (v) => {
    if (v == null || v === '') return '—';
    if (needsFallback()) {
        // Albanian convention: 1 250,00 €
        const [int, dec] = Math.abs(Number(v)).toFixed(2).split('.');
        return `${Number(v) < 0 ? '-' : ''}${int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')},${dec}\u00a0€`;
    }
    return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'EUR' }).format(Number(v));
};

/** Plain number (e.g. a quantity), up to two decimals, no trailing zeros. */
export const fmtNumber = (v) => {
    if (v == null || v === '') return '—';
    if (needsFallback()) return String(Math.round(Number(v) * 100) / 100).replace('.', ',');
    return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 2 }).format(Number(v));
};

export const fmtBytes = (n) => {
    if (n == null) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Parses YYYY-MM-DD as a local date (not UTC midnight). */
export const parseLocalDate = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};

/** Local date to YYYY-MM-DD. */
export const toIsoDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export const addDays = (date, n) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
};

export const startOfWeek = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = (d.getDay() + 6) % 7; // Monday = 0
    return addDays(d, -day);
};

export const todayIso = () => toIsoDate(new Date());

/** Date with custom Intl options in the active language. */
export const fmtDateWith = (date, options) => formatDate(date, options);

/** Planned time slot of a job: "09:00–11:00", "09:00" or '' (times arrive as HH:mm). */
export const fmtTimeRange = (job) => {
    if (!job?.scheduledStart) return '';
    return job.scheduledEnd ? `${job.scheduledStart}–${job.scheduledEnd}` : job.scheduledStart;
};
